import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { api, getJiraEpics, getJiraIssue } from '../../utils/api';
import MemberPicker from './MemberPicker';
import TagPicker from './TagPicker';
import { getBetTagSuggestions, getBetTeam } from '../../utils/shapeUpApi';

const filterEpics = createFilterOptions({
  stringify: (epic) => `${epic.key} ${epic.summary}`,
});

const ISSUE_KEY = /^[A-Za-z][A-Za-z0-9]*-\d+$/;

/**
 * Creates a bet, optionally linked to a Jira epic whose child issues become
 * the bet's scopes.
 *
 * The team is picked here too, from the cycle's roster only: staffing a bet with
 * somebody who is not working this cycle is the mistake worth making impossible.
 */
const BetDialog = ({
  open,
  onClose,
  onSubmit,
  organizationId,
  jiraEnabled = true,
  bet = null,
  rosterCandidates = [],
  orgCandidates = [],
  tagsByUser = null,
  tagSuggestions = [],
  slackAvailable = false,
}) => {
  const isEdit = Boolean(bet);
  const [form, setForm] = useState({ name: '', description: '', appetite: 'big', pitchUrl: '' });
  const [projectKey, setProjectKey] = useState('');
  const [epics, setEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [loadingEpics, setLoadingEpics] = useState(false);
  const [epicError, setEpicError] = useState(null);
  const [members, setMembers] = useState([]);
  const [tags, setTags] = useState([]);
  const [derivedTags, setDerivedTags] = useState([]);
  const [loadingDerived, setLoadingDerived] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [createSlackChannel, setCreateSlackChannel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadTeam = useCallback(async (betId) => {
    setLoadingTeam(true);
    try {
      const data = await getBetTeam(betId);
      setMembers(data.members || []);
    } catch {
      setMembers([]);
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  /**
   * Guesses about what this bet is, and on a bet nobody has tagged, they go straight
   * into the box.
   *
   * Seeding only when the bet has no tags is the whole reason this is safe to do
   * automatically. Once somebody has curated, reopening the dialog must not put back
   * what they deleted, so from then on the guesses stay as chips to accept by hand.
   */
  const loadDerivedTags = useCallback(async (betId, hasTags) => {
    setLoadingDerived(true);
    try {
      const data = await getBetTagSuggestions(betId);
      const suggested = data.suggestions || [];
      setDerivedTags(suggested);
      if (!hasTags && suggested.length) {
        setTags(suggested.map(tag => ({ slug: tag.slug, label: tag.label })));
      }
    } catch {
      setDerivedTags([]);
    } finally {
      setLoadingDerived(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(bet
      ? {
          name: bet.name || '',
          description: bet.description || '',
          appetite: bet.appetite || 'big',
          pitchUrl: bet.pitchUrl || '',
        }
      : { name: '', description: '', appetite: 'big', pitchUrl: '' });
    setSelectedEpic(null);
    setEpics([]);
    setError(null);
    setEpicError(null);
    // A channel is only ever offered on create, and only when Slack can actually
    // make one. Defaulting it off keeps bet creation from having a side effect
    // nobody asked for.
    setCreateSlackChannel(false);

    // Tags ride along in the serialized bet, so editing does not need a fetch the
    // way the team does.
    setTags(bet?.tags || []);
    setDerivedTags([]);

    if (bet) {
      loadTeam(bet.id);
      loadDerivedTags(bet.id, (bet.tags || []).length > 0);
    } else {
      setMembers([]);
    }

    // The epic picker only appears when creating a bet, so an edit does not need
    // the project key, and neither does manual mode.
    if (!jiraEnabled || bet) return;
    api.get('/users/me/settings/jira')
      .then(res => setProjectKey(res.data.jiraProjectKey || ''))
      .catch(() => setProjectKey(''));
  }, [open, jiraEnabled, bet, loadTeam, loadDerivedTags]);

  const loadEpics = useCallback(async () => {
    if (!projectKey.trim()) {
      setEpicError('Enter a Jira project key first.');
      return;
    }
    setLoadingEpics(true);
    setEpicError(null);
    try {
      const data = await getJiraEpics(projectKey.trim().toUpperCase(), organizationId);
      setEpics(data.epics || []);
      if (!data.epics?.length) {
        setEpicError(`No open epics found in ${projectKey.trim().toUpperCase()}.`);
      } else if (data.truncated) {
        setEpicError(`Showing the first ${data.epics.length} open epics. If yours is missing, type its key to look it up.`);
      }
    } catch (err) {
      setEpicError(err.response?.data?.message || 'Could not load epics from Jira.');
    } finally {
      setLoadingEpics(false);
    }
  }, [projectKey, organizationId]);

  // Fetch epics as soon as there is a project key to fetch for: on open (the
  // settings fetch above sets the key, which lands here) and again, debounced,
  // whenever somebody edits the key. The "Find epics" button stays as a manual
  // retry. Editing a bet has no epic picker, so it never runs here.
  useEffect(() => {
    if (!open || !jiraEnabled || isEdit || !projectKey.trim()) return;
    const timer = setTimeout(loadEpics, 400);
    return () => clearTimeout(timer);
  }, [open, jiraEnabled, isEdit, projectKey, loadEpics]);

  // The list only holds open epics from one project, so anything closed or
  // outside it stays unreachable. Typing a key fetches that issue directly.
  const lookUpEpic = async (key) => {
    setLoadingEpics(true);
    setEpicError(null);
    try {
      const issue = await getJiraIssue(key, organizationId);
      const issueType = issue.fields?.issuetype?.name;
      if (issueType && issueType.toLowerCase() !== 'epic') {
        setEpicError(`${issue.key} is a ${issueType}, not an epic. Link the epic that contains it instead.`);
        return;
      }
      const epic = {
        key: issue.key,
        summary: issue.fields?.summary || issue.key,
        status: issue.fields?.status?.name || 'Unknown',
      };
      setEpics(prev => (prev.some(e => e.key === epic.key) ? prev : [epic, ...prev]));
      setSelectedEpic(epic);
    } catch (err) {
      setEpicError(
        err.response?.status === 404
          ? `${key} does not exist, or your Jira account cannot see it.`
          : err.response?.data?.message || `Could not look up ${key}.`
      );
    } finally {
      setLoadingEpics(false);
    }
  };

  const onChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  /**
   * Everyone who could be put on this bet: the cycle's roster first, then the rest
   * of the organization.
   *
   * The roster used to be the whole list, which made the picker unusable on every
   * cycle that predates rosters existing, since those all have an empty one. Anyone
   * picked from outside it is added to the cycle by the server, so the roster stays
   * a superset of the bet teams without the user doing that bookkeeping.
   */
  const rosterIds = new Set(rosterCandidates.map(candidate => candidate.userId));
  const teamCandidates = [
    ...rosterCandidates,
    ...orgCandidates.filter(candidate => !rosterIds.has(candidate.userId)),
  ];
  const offRosterIds = new Set(
    orgCandidates
      .filter(candidate => !rosterIds.has(candidate.userId))
      .map(candidate => candidate.userId)
  );

  const submit = async () => {
    const name = form.name.trim() || selectedEpic?.summary || '';
    if (!name) {
      setError('Give the bet a name, or pick an epic to borrow its name.');
      return;
    }

    const pitchUrl = form.pitchUrl.trim();
    if (pitchUrl && !/^https?:\/\//i.test(pitchUrl)) {
      setError('The pitch link needs to start with http:// or https://');
      return;
    }

    const team = members.map(member => ({ userId: member.userId, role: member.role }));

    // Editing leaves the epic link alone; a create carries the picked epic so its
    // child issues can seed the scopes.
    const betTags = tags.map(tag => tag.label);

    const payload = isEdit
      ? {
          name,
          description: form.description.trim() || null,
          appetite: form.appetite,
          pitchUrl: pitchUrl || null,
          members: team,
          tags: betTags,
        }
      : {
          name,
          description: form.description.trim() || null,
          appetite: form.appetite,
          jiraEpicKey: selectedEpic?.key || null,
          jiraEpicSummary: selectedEpic?.summary || null,
          pitchUrl: pitchUrl || null,
          members: team,
          tags: betTags,
          createSlackChannel: createSlackChannel && slackAvailable,
        };

    setSaving(true);
    setError(null);
    try {
      await onSubmit(payload);
      // Remember the key this bet was created against so the next "New bet"
      // opens straight to its epics. A partial PUT, so URL/username/token stay
      // put; fire-and-forget since a failed remember must not fail the create.
      if (!isEdit && jiraEnabled && projectKey.trim()) {
        api.put('/users/me/settings/jira', { jiraProjectKey: projectKey.trim().toUpperCase() }).catch(() => {});
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || (isEdit ? 'Could not update the bet.' : 'Could not create the bet.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit bet' : 'New bet'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              fullWidth
              name="name"
              label="Bet name"
              placeholder="Rework the onboarding flow"
              value={form.name}
              onChange={onChange}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              name="description"
              label="Pitch summary (optional)"
              value={form.description}
              onChange={onChange}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="pitchUrl"
              label="Pitch link (optional)"
              placeholder="https://notion.so/the-full-pitch"
              helperText="Where the written pitch lives, if it is not in Jira."
              value={form.pitchUrl}
              onChange={onChange}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Appetite</Typography>
            <RadioGroup row name="appetite" value={form.appetite} onChange={onChange}>
              <FormControlLabel value="small" control={<Radio />} label="Small batch (a few days)" disabled={saving} />
              <FormControlLabel value="big" control={<Radio />} label="Big batch (full cycle)" disabled={saving} />
            </RadioGroup>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2">Who is on this bet</Typography>
            <Typography variant="caption" color="text.secondary">
              A whole team for the whole cycle. Anyone not on this cycle yet joins it
              when you pick them, and anyone on more than one bet shows up as
              splitting attention on the scoreboard.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <MemberPicker
              candidates={teamCandidates}
              value={members}
              onChange={setMembers}
              disabled={saving || loadingTeam}
              label="Add someone to this bet"
              tagsByUser={tagsByUser}
              offRosterIds={offRosterIds}
              emptyHint={loadingTeam ? 'Loading the team…' : 'Nobody on this bet yet.'}
              noCandidatesHint="Nobody in this organization yet."
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2">What kind of work is this</Typography>
            <Typography variant="caption" color="text.secondary">
              Tags go on the bet, not on people, so they stay true later. Search one
              and you get the bets and everyone who worked on them.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TagPicker
              value={tags}
              onChange={setTags}
              suggestions={tagSuggestions}
              derived={derivedTags}
              derivedLoading={loadingDerived}
              disabled={saving}
              helperText="Type to add. Existing tags are suggested as you go."
            />
          </Grid>

          {!isEdit && slackAvailable && (
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={createSlackChannel}
                    onChange={(e) => setCreateSlackChannel(e.target.checked)}
                    disabled={saving}
                  />
                )}
                label="Create a Slack channel and invite the team"
              />
            </Grid>
          )}

          {jiraEnabled && !isEdit && (
          <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Link a Jira epic</Typography>
            <Typography variant="caption" color="text.secondary">
              The epic's child issues become the scopes on this bet's hill chart.
            </Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Project key"
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value)}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadEpics}
              disabled={loadingEpics || saving}
              sx={{ height: '40px' }}
            >
              {loadingEpics ? 'Loading…' : 'Find epics'}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              options={epics}
              value={selectedEpic}
              disabled={saving}
              loading={loadingEpics}
              filterOptions={(options, params) => {
                const filtered = filterEpics(options, params);
                const typed = params.inputValue.trim().toUpperCase();
                if (ISSUE_KEY.test(typed) && !options.some(epic => epic.key === typed)) {
                  filtered.push({ key: typed, summary: 'Look up this issue in Jira', lookup: true });
                }
                return filtered;
              }}
              onChange={(event, value) => {
                if (value?.lookup) {
                  lookUpEpic(value.key);
                  return;
                }
                setSelectedEpic(value);
              }}
              getOptionLabel={(epic) => (epic.lookup ? epic.key : `${epic.key} — ${epic.summary}`)}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderOption={(props, epic) => (
                <Box component="li" {...props} key={epic.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                      {epic.key}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ flexGrow: 1 }}>
                      {epic.summary}
                    </Typography>
                    {!epic.lookup && <Chip label={epic.status} size="small" variant="outlined" />}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Epic"
                  placeholder={epics.length ? 'Search by key or name' : 'Find epics, or type a key like CSU-5170'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEpics ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {epicError && (
            <Grid item xs={12}>
              <Alert severity="info">{epicError}</Alert>
            </Grid>
          )}

          {selectedEpic && (
            <Grid item xs={12}>
              <Alert severity="success">
                Linking <strong>{selectedEpic.key}</strong> — {selectedEpic.summary}
              </Alert>
            </Grid>
          )}
          </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving
            ? (isEdit ? 'Saving…' : 'Creating…')
            : (isEdit ? 'Save changes' : 'Create bet')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BetDialog;
